import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchResults
 * @property {HTMLElement} resetButton
 * @property {HTMLElement[]} [resultsItems]
 * @property {HTMLElement} [recentlyViewedWrapper]
 * @property {HTMLElement[]} [recentlyViewedTitle]
 * @property {HTMLElement[]} [recentlyViewedItems]
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();
  #activeFetch = null;
  #emptyStateLoaded = false;
  #isKeyboardNavigation = false;

  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { dialog } = this;
    if (this.refs.searchInput.value.length > 0) this.#showResetButton();
    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      this.addEventListener('click', this.#handleModalClick, { signal });
    }
    this.#syncResultsState();
    if (this.#viewedProducts.length > 0) {
      requestIdleCallback(() => {
        if (this.isConnected) this.#loadEmptyState();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#activeFetch?.abort();
    this.#searchRequest.cancel();
    this.resetSearch.cancel();
  }

  #handleModalClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('button, a, input, select')) this.refs.searchInput.focus();
  };

  #handleKeyboardShortcut = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => {
    this.#searchRequest.cancel();
    this.resetSearch.cancel();
    void this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && this.#viewedProducts.length > 0) this.#loadEmptyState();
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded || this.refs.searchInput.value.trim()) return;
    this.#emptyStateLoaded = true;
    void this.#resetSearch();
  }

  get #viewedProducts() {
    try {
      const products = RecentlyViewed.getProducts();
      return Array.isArray(products) ? products.filter((id) => /^\d+$/.test(String(id))).slice(0, 4) : [];
    } catch {
      return [];
    }
  }

  get #allResultsItems() {
    return Array.from(this.refs.predictiveSearchResults.querySelectorAll('[role="option"]')).filter(
      (item) => item instanceof HTMLElement
    );
  }

  get #currentIndex() {
    return this.#allResultsItems.findIndex((item) => item.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(index) {
    let activeItem = null;
    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      const selected = itemIndex === index;
      item.setAttribute('aria-selected', String(selected));
      item.classList.toggle('keyboard-focus', selected && this.#isKeyboardNavigation);
      if (selected) activeItem = item;
    }
    const { searchInput } = this.refs;
    if (activeItem?.id) {
      searchInput.setAttribute('aria-activedescendant', activeItem.id);
      activeItem.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      searchInput.focus();
    } else {
      searchInput.removeAttribute('aria-activedescendant');
    }
  }

  get #matchesCurrentQuery() {
    const query = this.refs.predictiveSearchResults.querySelector('[data-search-query]');
    const term = this.refs.searchInput.value.trim();
    return query instanceof HTMLElement
      ? query.dataset.searchQuery?.trim().toLocaleLowerCase() === term.toLocaleLowerCase()
      : term === '';
  }

  #syncResultsState() {
    const { searchInput, predictiveSearchResults } = this.refs;
    const listbox = predictiveSearchResults.querySelector('#predictive-search-listbox');
    if (listbox) {
      searchInput.setAttribute('aria-controls', listbox.id);
      searchInput.setAttribute('aria-owns', listbox.id);
    } else {
      searchInput.removeAttribute('aria-controls');
      searchInput.removeAttribute('aria-owns');
    }
    searchInput.setAttribute('aria-expanded', String(this.#allResultsItems.length > 0 && this.#matchesCurrentQuery));
    this.#currentIndex = -1;
  }

  onSearchKeyDown = (event) => {
    if (event.target !== this.refs.searchInput) return;
    if (event.key === 'Tab') {
      this.#currentIndex = -1;
      return;
    }
    if (event.key === 'Escape') {
      if (!this.dialog) this.resetSearch();
      return;
    }
    const items = this.#allResultsItems;
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && items.length && this.#matchesCurrentQuery) {
      event.preventDefault();
      this.#isKeyboardNavigation = true;
      const current = this.#currentIndex;
      this.#currentIndex = event.key === 'ArrowDown'
        ? current < items.length - 1 ? current + 1 : 0
        : current > 0 ? current - 1 : items.length - 1;
      return;
    }
    if (event.key !== 'Enter' || event.isComposing) return;
    if (this.#matchesCurrentQuery && this.#currentIndex >= 0) {
      event.preventDefault();
      items[this.#currentIndex]?.querySelector('a')?.click();
      return;
    }
    const single = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
    if (this.#matchesCurrentQuery && single instanceof HTMLElement && single.dataset.singleResultUrl) {
      event.preventDefault();
      window.location.href = single.dataset.singleResultUrl;
      return;
    }
    event.preventDefault();
    this.refs.searchInput.form?.requestSubmit();
  };

  clearRecentlyViewedProducts(event) {
    event.stopPropagation();
    try {
      RecentlyViewed.clearProducts();
    } catch {
      return;
    }
    const wrapper = this.refs.recentlyViewedWrapper;
    if (!(wrapper instanceof HTMLElement)) return;
    wrapper.classList.add('removing');
    onAnimationEnd(wrapper, () => {
      wrapper.remove();
      this.#syncResultsState();
      this.refs.searchInput.focus();
    });
  }

  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) this.refs.searchInput.focus();
    this.#searchRequest.cancel();
    void this.#resetSearch();
  }, 100);

  search = (event) => {
    if (event.isComposing) return;
    this.#currentIndex = -1;
    this.#isKeyboardNavigation = false;
    this.#activeFetch?.abort();
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
    const searchTerm = this.refs.searchInput.value.trim();
    if (!searchTerm) {
      this.#searchRequest.cancel();
      void this.#resetSearch();
      return;
    }
    this.#showResetButton();
    this.#searchRequest(searchTerm);
  };

  #searchRequest = debounce((searchTerm) => {
    if (this.isConnected && this.refs.searchInput.value.trim() === searchTerm) void this.#getSearchResults(searchTerm);
  }, 200);

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'all');
    url.searchParams.set('resources[options][unavailable_products]', 'hide');
    const abortController = this.#createAbortController();
    const { predictiveSearchResults } = this.refs;
    predictiveSearchResults.setAttribute('aria-busy', 'true');
    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(new SearchUpdateEvent({ search: { query: searchTerm }, promise: deferred.promise }));
    try {
      const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url, abortController.signal);
      if (abortController.signal.aborted || !this.isConnected || this.refs.searchInput.value.trim() !== searchTerm) {
        deferred.resolve({ totalCount: 0 });
        return;
      }
      if (!markup || !new DOMParser().parseFromString(markup, 'text/html').querySelector('#predictive-search-results [data-search-query]')) {
        throw new Error('Missing search results');
      }
      morph(predictiveSearchResults, markup);
      this.#syncResultsState();
      this.#resetScrollPositions();
      deferred.resolve({ totalCount: this.#allResultsItems.length });
    } catch {
      deferred.resolve({ totalCount: 0 });
      if (!abortController.signal.aborted && this.isConnected) this.#showError();
    } finally {
      if (!abortController.signal.aborted) predictiveSearchResults.removeAttribute('aria-busy');
    }
  }

  async #getRecentlyViewedProductsMarkup(signal) {
    if (!this.dataset.sectionId) return null;
    const viewedProducts = this.#viewedProducts;
    if (!viewedProducts.length) return null;
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[unavailable_products]', 'hide');
    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url, signal);
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  #showError() {
    const message = document.createElement('p');
    message.className = 'predictive-search-results__no-results';
    message.setAttribute('role', 'status');
    message.textContent = this.dataset.searchError || '';
    this.refs.predictiveSearchResults.replaceChildren(message);
    this.#syncResultsState();
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const abortController = this.#createAbortController();
    this.#currentIndex = -1;
    searchInput.value = '';
    searchInput.setAttribute('aria-expanded', 'false');
    predictiveSearchResults.removeAttribute('aria-busy');
    this.#hideResetButton();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    try {
      const markup = await sectionRenderer.getSectionHTML('predictive-search-empty', false, url, abortController.signal);
      if (abortController.signal.aborted || !this.isConnected || searchInput.value.trim()) return;
      const empty = new DOMParser().parseFromString(markup, 'text/html').querySelector('.predictive-search-empty-section');
      if (!empty) throw new Error('Missing empty search state');
      if (this.#viewedProducts.length > 0) {
        const recentMarkup = await this.#getRecentlyViewedProductsMarkup(abortController.signal);
        if (recentMarkup) {
          const recent = new DOMParser().parseFromString(recentMarkup, 'text/html').getElementById('predictive-search-products');
          if (recent) empty.querySelector('#predictive-search-products')?.replaceWith(recent);
        }
      }
      if (abortController.signal.aborted || !this.isConnected || searchInput.value.trim()) return;
      morph(predictiveSearchResults, empty);
      this.#syncResultsState();
      this.#resetScrollPositions();
    } catch {
      if (!abortController.signal.aborted && this.isConnected) {
        this.#emptyStateLoaded = false;
        this.#showError();
      }
    }
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
